import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Badge, Button, Form, InputGroup, Modal, Spinner, Toast, ToastContainer } from 'react-bootstrap';
import { FiAlertTriangle, FiCheckCircle, FiCopy, FiExternalLink, FiMail, FiTrash2 } from 'react-icons/fi';
import { getAdminRoleFromToken } from '../../../utils/ecmwfExcelImport';
import {
    CG_EDITABLE_STATUSES,
    CG_STATUS_CONFIG,
    formatCgAmount,
    getCommercialGesturePermissions,
} from './commercialGestureUtils';

export const useCommercialGesturePermissions = () => {
    const accessToken = useSelector((state) => state.adminAuth?.accessToken);
    const role = getAdminRoleFromToken(accessToken || localStorage.getItem('admin_access_token'));
    return getCommercialGesturePermissions(role);
};

export const CommercialGestureStatusBadge = ({ status }) => {
    const config = CG_STATUS_CONFIG[status] || { label: status || '—', bg: 'light' };
    return (
        <Badge bg={config.bg} text={['light', 'warning'].includes(config.bg) ? 'dark' : undefined} className="cg-status-badge">
            {config.label}
        </Badge>
    );
};

/** Pile de toasts locale à la page ; accepte aussi un toast transmis par `navigate(..., { state: { toast } })`. */
export const useCgToasts = () => {
    const [toasts, setToasts] = useState([]);
    const idRef = useRef(0);
    const location = useLocation();
    const navigate = useNavigate();

    const push = useCallback((message, variant = 'success') => {
        idRef.current += 1;
        const id = idRef.current;
        setToasts((prev) => [...prev, { id, message, variant }]);
    }, []);

    const dismiss = useCallback((id) => {
        setToasts((prev) => prev.filter((toast) => toast.id !== id));
    }, []);

    useEffect(() => {
        const incoming = location.state?.toast;
        if (!incoming) return;
        push(incoming.message, incoming.variant || 'success');
        navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }, [location.state, location.pathname, location.search, navigate, push]);

    return { toasts, push, dismiss };
};

export const CgToastStack = ({ toasts, onDismiss }) => (
    <ToastContainer position="top-end" className="p-3 cg-toast-stack" containerPosition="fixed">
        {toasts.map((toast) => (
            <Toast
                key={toast.id}
                bg={toast.variant === 'danger' ? 'danger' : 'success'}
                onClose={() => onDismiss(toast.id)}
                autohide
                delay={toast.variant === 'danger' ? 6000 : 4000}
            >
                <Toast.Body className="text-white d-flex align-items-center gap-2" role="status" aria-live="polite">
                    {toast.variant === 'danger'
                        ? <FiAlertTriangle aria-hidden="true" />
                        : <FiCheckCircle aria-hidden="true" />}
                    <span className="flex-grow-1">{toast.message}</span>
                    <button
                        type="button"
                        className="btn-close btn-close-white"
                        aria-label="Fermer"
                        onClick={() => onDismiss(toast.id)}
                    />
                </Toast.Body>
            </Toast>
        ))}
    </ToastContainer>
);

export const CgSendRibLinkModal = ({ show, gesture, sending, onCancel, onConfirm }) => {
    const confirmLockRef = useRef(false);
    const isResend = gesture?.status === 'send';

    useEffect(() => {
        if (!show || !sending) confirmLockRef.current = false;
    }, [show, sending]);

    const handleConfirm = (event) => {
        event?.preventDefault?.();
        event?.stopPropagation?.();
        if (sending || confirmLockRef.current) return;
        confirmLockRef.current = true;
        onConfirm();
    };

    return (
        <Modal
            show={show}
            onHide={sending ? undefined : onCancel}
            backdrop={sending ? 'static' : true}
            keyboard={!sending}
            centered
        >
            <Modal.Header closeButton={!sending}>
                <Modal.Title>{isResend ? 'Renvoyer le lien RIB' : 'Envoyer le lien RIB'}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                {isResend ? 'Renvoyer' : 'Envoyer'} le lien RIB de{' '}
                <strong>{formatCgAmount(gesture?.amount)}</strong>
                {' '}à <strong>{gesture?.mail || '—'}</strong> ?
            </Modal.Body>
            <Modal.Footer>
                <Button type="button" variant="outline-secondary" onClick={onCancel} disabled={sending} autoFocus>
                    Annuler
                </Button>
                <Button type="button" variant="primary" onClick={handleConfirm} disabled={sending}>
                    {sending ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Envoi…
                        </>
                    ) : (
                        <>
                            <FiMail className="me-2" aria-hidden="true" />
                            {isResend ? 'Renvoyer' : 'Envoyer'}
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export const CgDeleteConfirmModal = ({ show, gesture, deleting, onCancel, onConfirm }) => (
    <Modal
        show={show}
        onHide={deleting ? undefined : onCancel}
        backdrop={deleting ? 'static' : true}
        keyboard={!deleting}
        centered
    >
        <Modal.Header closeButton={!deleting}>
            <Modal.Title>Supprimer le geste commercial</Modal.Title>
        </Modal.Header>
        <Modal.Body>
            Supprimer le geste commercial de la police{' '}
            <strong>{gesture?.policy_code || '—'}</strong> ({formatCgAmount(gesture?.amount)}) ?
            Cette action est irréversible.
        </Modal.Body>
        <Modal.Footer>
            <Button variant="outline-secondary" onClick={onCancel} disabled={deleting} autoFocus>
                Annuler
            </Button>
            <Button variant="danger" onClick={onConfirm} disabled={deleting}>
                {deleting ? (
                    <>
                        <Spinner size="sm" className="me-2" />
                        Suppression…
                    </>
                ) : (
                    <>
                        <FiTrash2 className="me-2" aria-hidden="true" />
                        Supprimer
                    </>
                )}
            </Button>
        </Modal.Footer>
    </Modal>
);

export const CgAmountField = ({ id, value, onChange, error, disabled }) => (
    <Form.Group className="mb-3" controlId={id}>
        <Form.Label>Montant (€)</Form.Label>
        <InputGroup hasValidation>
            <Form.Control
                type="number"
                inputMode="decimal"
                min="0"
                step="0.01"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                isInvalid={Boolean(error)}
                disabled={disabled}
                placeholder="150.00"
                required
            />
            <InputGroup.Text>€</InputGroup.Text>
            <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
        </InputGroup>
    </Form.Group>
);

export const CgPolicyPreview = ({ policy }) => {
    if (!policy) return null;
    return (
        <dl className="cg-readonly-grid">
            <div>
                <dt>Code police</dt>
                <dd>{policy.policy_code || '—'}</dd>
            </div>
            <div>
                <dt>Réservation</dt>
                <dd>{policy.reservation_number || '—'}</dd>
            </div>
            <div>
                <dt>Prénom</dt>
                <dd>{policy.first_name || '—'}</dd>
            </div>
            <div>
                <dt>Nom</dt>
                <dd>{policy.last_name || '—'}</dd>
            </div>
            <div>
                <dt>Mail</dt>
                <dd>{policy.mail || '—'}</dd>
            </div>
        </dl>
    );
};

export const CgRibLink = ({ link, onCopied, onCopyError }) => {
    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(link);
            onCopied?.();
        } catch {
            onCopyError?.();
        }
    };

    return (
        <div className="cg-rib-link">
            <div className="cg-rib-link__label">Lien RIB</div>
            {link ? (
                <InputGroup>
                    <Form.Control
                        readOnly
                        value={link}
                        title={link}
                        aria-label="Lien RIB"
                        className="cg-rib-link__input"
                        onFocus={(e) => e.target.select()}
                    />
                    <Button variant="outline-secondary" onClick={handleCopy} title="Copier le lien">
                        <FiCopy className="me-1" aria-hidden="true" />
                        Copier
                    </Button>
                    <Button
                        variant="outline-primary"
                        href={link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title="Ouvrir le lien dans un nouvel onglet"
                    >
                        <FiExternalLink className="me-1" aria-hidden="true" />
                        Ouvrir
                    </Button>
                </InputGroup>
            ) : (
                <div className="text-muted">Aucun lien RIB généré pour ce geste commercial.</div>
            )}
        </div>
    );
};

export const CgStatusField = ({ id, value, onChange, error, disabled }) => (
    <Form.Group className="mb-3" controlId={id}>
        <Form.Label>Statut</Form.Label>
        <Form.Select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            isInvalid={Boolean(error)}
            disabled={disabled}
        >
            {value === 'send' && (
                <option value="send" disabled>{CG_STATUS_CONFIG.send.label}</option>
            )}
            {CG_EDITABLE_STATUSES.map((status) => (
                <option key={status} value={status}>{CG_STATUS_CONFIG[status].label}</option>
            ))}
        </Form.Select>
        <Form.Control.Feedback type="invalid">{error}</Form.Control.Feedback>
    </Form.Group>
);

/** Formulaire amount + status ; ne remonte que les champs modifiés. */
export const useCgEditForm = (gesture) => {
    const [amount, setAmount] = useState('');
    const [status, setStatus] = useState('created');

    useEffect(() => {
        if (!gesture) return;
        setAmount(gesture.amount === null || gesture.amount === undefined ? '' : Number(gesture.amount).toFixed(2));
        setStatus(gesture.status || 'created');
    }, [gesture]);

    const amountNumber = Number(String(amount).replace(',', '.'));
    const amountChanged = gesture && String(amount).trim() !== ''
        && (!Number.isFinite(amountNumber) || amountNumber !== Number(gesture.amount));
    const statusChanged = gesture && status !== gesture.status;

    return {
        amount,
        setAmount,
        status,
        setStatus,
        amountChanged: Boolean(amountChanged),
        statusChanged: Boolean(statusChanged),
        hasChanges: Boolean(amountChanged || statusChanged),
    };
};
